import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_HIGH,
  $getRoot,
  $isElementNode,
  type EditorState,
  type LexicalNode,
} from 'lexical';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { $isImageNode } from '../ImageNode';

function hasImageInClipboard(event: ClipboardEvent): boolean {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return false;

  const files = Array.from(clipboardData.files ?? []);
  if (files.some((file) => file.type.startsWith('image/'))) {
    return true;
  }

  const html = clipboardData.getData('text/html');
  if (html && /<(img|figure)[^>]*>/i.test(html)) {
    return true;
  }

  const plainText = clipboardData.getData('text/plain');
  if (plainText && /^data:image\//i.test(plainText.trim())) {
    return true;
  }

  return false;
}

const collectImageNodeKeys = (nodes: LexicalNode[]): Set<string> => {
  const keys = new Set<string>();
  const stack = [...nodes];

  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;

    if ($isImageNode(node)) {
      keys.add(node.getKey());
      continue;
    }

    if ($isElementNode(node)) {
      stack.push(...node.getChildren());
    }
  }

  return keys;
};

const getImageNodeKeysFromState = (editorState: EditorState): Set<string> =>
  editorState.read(() => collectImageNodeKeys($getRoot().getChildren()));

export function ImagePasteBlockerPlugin() {
  const { t } = useTranslation('knowledge_base');
  const [editor] = useLexicalComposerContext();

  const warningMessage = useMemo(() => t('article.markdown_editor.image_paste_error'), [t]);

  useEffect(
    () =>
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          const clipboardEvent = event as ClipboardEvent | null;
          if (!clipboardEvent) return false;
          if (!hasImageInClipboard(clipboardEvent)) return false;

          const existingImageKeys = getImageNodeKeysFromState(editor.getEditorState());

          toast.error(warningMessage);

          setTimeout(() => {
            editor.update(() => {
              const root = $getRoot();
              const stack: LexicalNode[] = [...root.getChildren()];
              const nodesToRemove: LexicalNode[] = [];

              while (stack.length > 0) {
                const node = stack.pop();
                if (!node) continue;

                if ($isImageNode(node) && !existingImageKeys.has(node.getKey())) {
                  nodesToRemove.push(node);
                  continue;
                }

                if ($isElementNode(node)) {
                  stack.push(...node.getChildren());
                }
              }

              nodesToRemove.forEach((node) => {
                node.remove();
              });
            });
          }, 0);

          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    [editor, warningMessage],
  );

  return null;
}

export default ImagePasteBlockerPlugin;
